import { awsResources } from '@/awsCodes/resourceParams';
import axios from 'axios';
import { IBaseUrl } from '../interfaces/IBaseUrl';
import { getAuthInfo } from '@/awsCodes/awsUtils';
import { removeTokenIfExpired } from '@/appLogic/tokenAroundManagement';
import { displaySessionErrorInfo } from '@/corePagesModule/components/InfoModal/displaySessionErrorInfo';
import { toGzipRequest } from './gzipRequestInterceptor';
import { redirectIfInMaintenanceMode } from './maintenanceInterceptor';

/**
 * カスタムAxiosインスタンスの作成(リクエストの直前でインスタンスを生成する想定)
 * @param baseUrl - 接続先の情報
 * @param timeout - タイムアウト時間(ms)
 */
export const customAxiosInstance = async (baseUrl: IBaseUrl, timeout?: number) => {
  const basePath = awsResources.baseUrl[baseUrl];
  // トークンの取得
  const idToken = await getAuthInfo();

  if (!idToken) {
    // トークンが取得できなかったらエラーにする
    throw new Error('refreshToken expired');
  }

  // トークンの有効期限を確認し、有効期限内でない場合はトークン情報を削除した上でログイン画面にリダイレクトする
  const isTokenValid = await removeTokenIfExpired();
  if (!isTokenValid) {
    // トークンが期限切れの場合はセッションエラー情報を表示
    await displaySessionErrorInfo();
    return;
  }

  const timeoutValue = timeout ?? 30000; // API Gatewayのデフォルトタイムアウト値に合わせる

  const axiosInstance = axios.create({
    baseURL: basePath,
    timeout: timeoutValue,
    headers: { Authorization: idToken.idToken },
  });

  // 圧縮が必要なリクエストを圧縮する
  axiosInstance.interceptors.request.use((config) => toGzipRequest(config));

  // メンテモードレスポンスの処理
  axiosInstance.interceptors.response.use((response) => {
    if (redirectIfInMaintenanceMode(response.data)) {
      return new Promise<never>(() => {});
    }
    return response;
  });

  return axiosInstance;
};
